import type { RosterItemResponse } from '@on3/api';
import { formatMillions } from '@on3/ui-lib/utils/formatters';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { getAbbreviatedRank } from 'components/Nil/helpers';
import { AthleteVerified } from 'components/UI/AthleteVerified/AthleteVerified';
import { TextLink } from 'components/UI/Link/TextLink';
import { StarRating } from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';

import styles from './TeamRoster.module.scss';

export const TeamRosterItem = ({
  player,
  organization,
  rosterRating,
  rating,
  nilValue,
}: RosterItemResponse) => {
  const {
    defaultAsset,
    jerseyNumber,
    height,
    weight,
    hometown,
    highSchool,
    fullName,
    classRank,
    athleteVerified,
    slug,
    position,
    age,
  } = player || {};

  return (
    <li className={styles.playerItem}>
      <Text className={styles.jerseyNumber} component="span">
        {jerseyNumber}
      </Text>
      <On3Avatar
        altText={defaultAsset?.altText}
        className={styles.playerAvatar}
        domain={defaultAsset?.domain}
        radiusFive
        source={defaultAsset?.source}
      />
      <div className={styles.playerDetailsContainer}>
        <div className={styles.playerNameContainer}>
          <TextLink
            className={styles.playerName}
            color="primary"
            href={`/db/${slug}/`}
            underline="none"
            variant="h5"
          >
            {fullName}
          </TextLink>
          {athleteVerified && <AthleteVerified />}
        </div>

        <div className={styles.hometownContainer}>
          <TextLink
            className={styles.highSchool}
            href={`/high-school/${highSchool?.urlSlug}/`}
          >
            {highSchool?.name}
          </TextLink>
          <Text className={styles.homeTown} component="span">
            ({hometown?.name})
          </Text>
        </div>
        <div className={styles.playerVitalsContainer}>
          <Text className={styles.position} component="span">
            {position?.abbr ?? '-'}
          </Text>
          <Text className={styles.height} component="span">
            {height ?? '-'}
          </Text>

          <Text className={styles.weight} component="span">
            {weight ?? '-'}
          </Text>
          {age && (
            <Text className={styles.age} component="span">
              {age}
              <span aria-label="years">yrs</span>
            </Text>
          )}
        </div>
      </div>
      <div className={styles.classContainer}>
        <TeamAvatar
          height={25}
          slug={organization?.slug}
          src={organization?.assetUrl}
          width={25}
        />
        <Text className={styles.classRank} component="span">
          {classRank ? getAbbreviatedRank(classRank) : '-'}
        </Text>
      </div>
      <div className={styles.ratingsContainer}>
        <div className={styles.collegeRatingContainer}>
          <StarRating
            href={`/db/${slug}/industry-comparison/`}
            isConsensus
            large
            position={rosterRating?.positionAbbr}
            rating={rosterRating?.rating}
            sport={rosterRating?.sport?.name}
            stars={rosterRating?.stars}
            state={rosterRating?.stateAbbr}
            year={`${rosterRating?.year}`}
          />
          <TeamAvatar
            className={styles.ratingTeamLogo}
            height={18}
            slug={organization?.slug}
            src={organization?.assetUrl}
            width={18}
          />
        </div>
        <StarRating
          href={`/db/${slug}/industry-comparison/`}
          isBoldRating={false}
          isConsensus
          position={rating?.positionAbbr}
          rating={rating?.consensusRating}
          sport={rating?.sport?.name}
          stars={rating?.consensusStars}
          state={rating?.stateAbbr}
          year={`${rating?.year}`}
        />
      </div>

      <div className={styles.nilValueContainer}>
        <span className={styles.lblNilValue}>NIL Value:</span>
        <Text className={styles.totalValue}>
          {nilValue?.totalValue ? (
            <span>${formatMillions(nilValue.totalValue)}</span>
          ) : (
            <span className={styles.valuationUndisclosed}>undisclosed</span>
          )}
        </Text>
      </div>

      <span className={styles.desktopPosition}>{position?.abbr}</span>
      <span className={styles.desktopAge}>{age ?? '-'}</span>
    </li>
  );
};
